/**
 * playback-sim.js — window.PlaybackSim
 *
 * Block 7 of the mastering checklist ("проверка на системах") turned into what
 * it actually is: AUDITIONING, not measurement. You cannot measure "утомляет
 * за 5 минут" or "жужжит корпус машины"; you can let someone hear the mix the
 * way those systems will render it and judge for themselves.
 *
 * So this module never produces a verdict. It builds a WebAudio node chain
 * that colours playback, and the page says "послушайте" — never "провал".
 *
 * ── Honesty about what these profiles are ─────────────────────────────────
 * `quiet` is exact: it is the ISO 226:2003 equal-loudness difference between
 * two loudness levels, from the published af / Lu / Tf tables. Turning the
 * volume down really does cost you bass and top, by this much.
 *
 * The device profiles are deliberately SIMPLE MODELS, not measurements of any
 * particular handset — there is no single authoritative curve for "a phone",
 * and pretending otherwise would be the same false precision that got the
 * codec-true-peak check rejected. Each profile documents its model in code and
 * the page says so in `sys_note`, so nobody reads it as "your device".
 *
 * Every chain's real transfer function is asserted against its documented
 * target by tests/playwright/playback-sim.mjs — a profile that does not do
 * what it says is the failure mode this module has to avoid, and it already
 * caught one: `quiet` shipped with the ISO curve the wrong way round and was
 * BOOSTING the low end by 16.5 dB under a label promising the opposite.
 */
(function () {
    'use strict';

    /* ── ISO 226:2003 normal equal-loudness-level contours ─────────────────
     * Lp(f, Ln) = (10/af)·log10(Af) − Lu + 94,
     *   Af = 4.47e-3·(10^(0.025·Ln) − 1.15) + (0.4·10^((Tf+Lu)/10 − 9))^af
     * Self-checking property used by the tests: at 1 kHz, Lp must equal Ln.
     */
    var ISO_F  = [20, 25, 31.5, 40, 50, 63, 80, 100, 125, 160, 200, 250, 315, 400,
                  500, 630, 800, 1000, 1250, 1600, 2000, 2500, 3150, 4000, 5000,
                  6300, 8000, 10000, 12500];
    var ISO_AF = [0.532, 0.506, 0.480, 0.455, 0.432, 0.409, 0.387, 0.367, 0.349,
                  0.330, 0.315, 0.301, 0.288, 0.276, 0.267, 0.259, 0.253, 0.250,
                  0.246, 0.244, 0.243, 0.243, 0.243, 0.242, 0.242, 0.245, 0.254,
                  0.271, 0.301];
    var ISO_LU = [-31.6, -27.2, -23.0, -19.1, -15.9, -13.0, -10.3, -8.1, -6.2,
                  -4.5, -3.1, -2.0, -1.1, -0.4, 0.0, 0.3, 0.5, 0.0, -2.7, -4.1,
                  -1.0, 1.7, 2.5, 1.2, -2.1, -7.1, -11.2, -10.7, -3.1];
    var ISO_TF = [78.5, 68.7, 59.5, 51.1, 44.0, 37.5, 31.5, 26.5, 22.1, 17.9,
                  14.4, 11.4, 8.6, 6.2, 4.4, 3.0, 2.2, 2.4, 3.5, 1.7, -1.3,
                  -4.2, -6.0, -5.4, -1.5, 6.0, 12.6, 13.9, 12.3];

    // SPL needed at ISO band `i` for loudness level `phon`.
    function isoSpl(i, phon) {
        var af = ISO_AF[i], Lu = ISO_LU[i], Tf = ISO_TF[i];
        var Af = 4.47e-3 * (Math.pow(10, 0.025 * phon) - 1.15)
               + Math.pow(0.4 * Math.pow(10, (Tf + Lu) / 10 - 9), af);
        return (10 / af) * Math.log10(Af) - Lu + 94;
    }

    /**
     * Raw ISO difference: how much MORE level each band needs at `toPhon`
     * than at `fromPhon` to be heard equally loud. Positive in the bass,
     * because quiet listening costs you bass perception.
     *
     * ⚠️ This is NOT the simulation curve — see simulationCurve() below. The
     * two differ by a sign, and shipping the wrong one boosted the low end by
     * +16.5 dB while the label said "тихая громкость". Caught by measuring
     * the rendered transfer function, not by reading the code.
     */
    function equalLoudnessDelta(fromPhon, toPhon) {
        var ref = (isoSpl(17, toPhon) - isoSpl(17, fromPhon)); // index 17 = 1 kHz
        var out = [];
        for (var i = 0; i < ISO_F.length; i++) {
            out.push({ hz: ISO_F[i], db: (isoSpl(i, toPhon) - isoSpl(i, fromPhon)) - ref });
        }
        return out;
    }

    /**
     * The curve to APPLY so that music played at the listener's current level
     * sounds the way it would at `toPhon`: the inverse of the compensation
     * they would need. Bass and top come DOWN.
     */
    function simulationCurve(fromPhon, toPhon) {
        return equalLoudnessDelta(fromPhon, toPhon).map(function (d) {
            return { hz: d.hz, db: -d.db };
        });
    }

    /* ── Profiles ──────────────────────────────────────────────────────────
     * `bands` are peaking/shelf/pass filters applied in series. `mono` sums
     * the channels. `describe` is user-facing and states that this is a model.
     */
    var PROFILES = {
        phone: {
            key: 'phone',
            mono: true,     // a single-speaker phone is mono by construction
            filters: [
                // Small drivers in a small box: nothing usable below ~500 Hz.
                { type: 'highpass', hz: 500, q: 0.7 },
                { type: 'highpass', hz: 500, q: 0.7 },
                { type: 'highpass', hz: 500, q: 0.7 },   // 6th order ≈ 36 dB/oct
                // The midrange horn resonance that makes phones shouty.
                // Band-averaged lift always reads lower than the filter gain, so
                // the model uses the peakiness small drivers actually have.
                { type: 'peaking', hz: 2500, q: 1.2, db: 9 },
                { type: 'peaking', hz: 4000, q: 1.6, db: 5 },
                { type: 'lowpass', hz: 12000, q: 0.7 }
            ]
        },
        laptop: {
            key: 'laptop',
            mono: false,
            filters: [
                { type: 'highpass', hz: 250, q: 0.7 },
                { type: 'highpass', hz: 250, q: 0.7 },   // 4th order
                { type: 'peaking', hz: 1800, q: 1.0, db: 4 },
                { type: 'peaking', hz: 320, q: 1.5, db: -4 },  // boxy dip
                { type: 'lowpass', hz: 15000, q: 0.7 }
            ]
        },
        car: {
            key: 'car',
            mono: false,
            filters: [
                // Cabin gain plus a sub: the low end arrives with authority,
                // while road noise masks the quiet detail above it.
                { type: 'lowshelf', hz: 90, db: 7 },
                { type: 'peaking', hz: 55, q: 1.4, db: 4 },
                { type: 'peaking', hz: 250, q: 1.0, db: -3 },
                { type: 'peaking', hz: 3500, q: 1.2, db: 3 },
                { type: 'lowpass', hz: 16000, q: 0.7 }
            ]
        },
        mono: { key: 'mono', mono: true, filters: [] },
        quiet: {
            key: 'quiet',
            mono: false,
            // Built from ISO 226 at call time — see buildQuiet().
            filters: null,
            fromPhon: 85, toPhon: 55
        }
    };

    // Turn the ISO delta into a peaking-filter ladder. One filter per octave
    // band is enough: the contour is smooth, and denser filtering only adds
    // phase smear without changing what the listener hears.
    function buildQuiet(p) {
        var delta = simulationCurve(p.fromPhon, p.toPhon);
        var picks = [31.5, 63, 125, 250, 500, 2000, 4000, 8000, 12500];
        var out = [];
        picks.forEach(function (hz) {
            var nearest = delta[0], best = Infinity;
            delta.forEach(function (d) {
                var dist = Math.abs(Math.log(d.hz / hz));
                if (dist < best) { best = dist; nearest = d; }
            });
            if (Math.abs(nearest.db) >= 0.5) {
                out.push({ type: 'peaking', hz: hz, q: 1.1, db: nearest.db });
            }
        });
        return out;
    }

    function filtersFor(key) {
        var p = PROFILES[key];
        if (!p) return null;
        return p.filters || buildQuiet(p);
    }

    /**
     * Build the chain inside `ctx` and return { input, output, mono }.
     * The caller wires its source into `input` and `output` to the
     * destination, so the same profile works on a live player and inside an
     * OfflineAudioContext for the tests.
     */
    function createChain(ctx, key) {
        var p = PROFILES[key];
        if (!p) return null;
        var specs = filtersFor(key);
        var input = ctx.createGain(), node = input;

        if (p.mono) {
            // Sum to mono by folding both channels through a merger fed from
            // one splitter output pair at equal gain.
            var splitter = ctx.createChannelSplitter(2);
            var merger = ctx.createChannelMerger(2);
            var gL = ctx.createGain(), gR = ctx.createGain();
            gL.gain.value = 0.5; gR.gain.value = 0.5;
            input.connect(splitter);
            splitter.connect(gL, 0); splitter.connect(gR, 1);
            gL.connect(merger, 0, 0); gR.connect(merger, 0, 0);
            gL.connect(merger, 0, 1); gR.connect(merger, 0, 1);
            node = merger;
        }

        specs.forEach(function (f) {
            var b = ctx.createBiquadFilter();
            b.type = f.type;
            b.frequency.value = f.hz;
            if (f.q != null) b.Q.value = f.q;
            if (f.db != null) b.gain.value = f.db;
            node.connect(b);
            node = b;
        });

        var out = ctx.createGain();
        // Device models add midrange energy; trim so switching profiles is a
        // change of CHARACTER and not a jump in level, which would dominate
        // the comparison and tell the listener nothing.
        out.gain.value = (key === 'phone') ? 0.7 : (key === 'car' ? 0.55 : 1.0);
        node.connect(out);
        return { input: input, output: out, mono: !!p.mono };
    }

    window.PlaybackSim = {
        keys: ['phone', 'laptop', 'car', 'mono', 'quiet'],
        createChain: createChain,
        filtersFor: filtersFor,
        equalLoudnessDelta: equalLoudnessDelta,
        simulationCurve: simulationCurve,
        isoSpl: isoSpl,
        ISO_F: ISO_F,
        version: '1.0.0'
    };
})();
